import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard, ShoppingCart, Tag, Warehouse,
  Percent, Users, Truck, ClipboardList, Receipt, LogOut,
  Factory, Settings, PanelLeftClose, PanelLeftOpen,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { hasFullAccess } from '../utils/permissions';
import useSettings from '../hooks/useSettings';

const COLLAPSED_KEY = 'sidebar-collapsed';

function readCollapsed() {
  try { return localStorage.getItem(COLLAPSED_KEY) === '1'; } catch { return false; }
}

function getDisplayName(user) {
  if (!user) return '';
  if (user.shopName) return user.shopName;
  if (user.vendorId && user.username.startsWith(user.vendorId + '_')) {
    return user.username.slice(user.vendorId.length + 1);
  }
  return user.username;
}

export default function Sidebar() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const role = user?.role;
  const displayName = getDisplayName(user);
  const { simpleView } = useSettings();
  const [collapsed, setCollapsed] = useState(readCollapsed);

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      try { localStorage.setItem(COLLAPSED_KEY, c ? '0' : '1'); } catch { /* ignore */ }
      return !c;
    });
  };

  const ALL_NAV = [
    { to: '/',                label: t('nav.dashboard'),      icon: LayoutDashboard, end: true,  fullOnly: false },
    { to: '/orders',          label: t('nav.orders'),          icon: ShoppingCart,               fullOnly: false },
    { to: '/categories',      label: t('nav.categories'),      icon: Tag,                        fullOnly: true  },
    { to: '/inventory',       label: t('nav.inventory'),       icon: Warehouse,                  fullOnly: true  },
    { to: '/offers',          label: t('nav.offers'),          icon: Percent,                    fullOnly: true  },
    { to: '/customers',       label: t('nav.customers'),       icon: Users,                      fullOnly: true  },
    { to: '/sourcers',        label: t('nav.sourcers'),        icon: Truck,                      fullOnly: true, simpleHidden: true },
    { to: '/sourcing-orders', label: t('nav.sourcingOrders'),  icon: ClipboardList,              fullOnly: true, simpleHidden: true },
    { to: '/balance-sheet',   label: t('nav.balanceSheet'),    icon: Receipt,                    fullOnly: true  },
    { to: '/manufacturing',   label: t('nav.manufacturing'),   icon: Factory,                    fullOnly: true, simpleHidden: true },
  ];

  const visibleNav = ALL_NAV.filter(item =>
    (!item.fullOnly || hasFullAccess(role)) && !(simpleView && item.simpleHidden)
  );

  const itemLayout = collapsed ? 'justify-center px-0' : 'gap-3 px-3';
  const navClass = ({ isActive }) =>
    `flex items-center ${itemLayout} py-2 rounded-lg text-sm transition-colors ${
      isActive
        ? 'bg-blue-50 text-blue-700 font-medium'
        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
    }`;
  const toggleLabel = collapsed ? t('nav.expand') : t('nav.collapse');
  const ToggleIcon = collapsed ? PanelLeftOpen : PanelLeftClose;

  return (
    <aside className={`${collapsed ? 'w-16' : 'w-60'} shrink-0 h-screen flex flex-col bg-white border-r border-gray-200 overflow-hidden transition-[width] duration-200`}>
      {/* Brand */}
      <div className={`py-5 border-b border-gray-100 flex items-center ${collapsed ? 'justify-center px-2' : 'gap-2 px-5'}`}>
        {!collapsed && (
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-gray-900 truncate">{displayName}</p>
            <p className="text-xs text-gray-400 mt-0.5 truncate">
              {role === 'VENDOR' ? `@${user?.username}` : role?.replace('_', ' ').toLowerCase()}
            </p>
          </div>
        )}
        <button
          onClick={toggleCollapsed}
          title={toggleLabel}
          aria-label={toggleLabel}
          className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors shrink-0"
        >
          <ToggleIcon size={16} strokeWidth={1.75} />
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-3 px-3">
        <ul className="space-y-0.5">
          {visibleNav.map(({ to, label, icon: Icon, end }) => (
            <li key={to}>
              <NavLink to={to} end={end} title={collapsed ? label : undefined} className={navClass}>
                <Icon size={16} strokeWidth={1.75} className="shrink-0" />
                {!collapsed && <span className="truncate">{label}</span>}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* Settings — everyone (tabs inside are role-gated) */}
      <div className="px-3 pb-1">
        <NavLink to="/settings" title={collapsed ? t('nav.settings') : undefined} className={navClass}>
          <Settings size={16} strokeWidth={1.75} className="shrink-0" />
          {!collapsed && <span className="truncate">{t('nav.settings')}</span>}
        </NavLink>
      </div>

      {/* Logout */}
      <div className="p-3 border-t border-gray-100">
        <button
          onClick={logout}
          title={collapsed ? t('nav.signOut') : undefined}
          className={`flex items-center ${itemLayout} w-full py-2 rounded-lg text-sm text-gray-500 hover:bg-red-50 hover:text-red-600 transition-colors`}
        >
          <LogOut size={16} strokeWidth={1.75} className="shrink-0" />
          {!collapsed && <span className="truncate">{t('nav.signOut')}</span>}
        </button>
      </div>
    </aside>
  );
}
